'use client';

import {
  ComponentBuilder,
  ComponentBuilderFetchDataInputs,
} from 'strapi/componentBuilder';
import { pageTypes } from 'strapi/pageTypes';

import KeyPointsColumn from '../shared/KeyPointsColumn/KeyPointsColumn';
import { KeyPointsProps } from '../shared/KeyPointsColumn/types';

import {
  isMicroAppPageType,
  KeyPointsColumnAutoFilledStrapiComponent,
} from './types';
import { fetchMicroAppPages, fetchFeaturePages } from './utils/fetchPageData';

/**
 * Builds a component that displays related pages based on the current page type.
 * For micro apps: shows other micro app pages
 * For feature/solution pages: shows related feature pages
 */
export class KeyPointsColumnAutoFilledComponentBuilder extends ComponentBuilder {
  public key = 'text-with-image.media-cards-row-auto-filled' as const;

  async fetchData(inputs: ComponentBuilderFetchDataInputs) {
    const fieldName = this.getFieldNameSymbol(inputs.componentData);

    const page = await inputs.fetchManager.addRequestOptionsAndWaitForResponse<{
      fieldName: KeyPointsColumnAutoFilledStrapiComponent;
    }>({
      populate: {
        [fieldName]: true,
      },
    });

    const componentData = page[fieldName];
    if (!componentData) {
      return undefined;
    }

    const keyPoints: KeyPointsProps[] = [];

    // Check if current page is one of our micro apps
    if (isMicroAppPageType(inputs.pageKey)) {
      const fetchedKeyPoints = await fetchMicroAppPages(inputs.pageKey);
      keyPoints.push(...fetchedKeyPoints);
    }

    // Check if current page is one of our feature/solution pages
    if (
      inputs.pageKey === pageTypes.FEATURES_CHILD_COLLECTION_TYPE ||
      inputs.pageKey === pageTypes.SOLUTIONS_CHILD_COLLECTION_TYPE
    ) {
      const fetchedKeyPoints = await fetchFeaturePages(
        inputs.pageKey,
        page.path
      );
      keyPoints.push(...fetchedKeyPoints);
    }

    if (keyPoints.length === 0) {
      return undefined;
    }

    return {
      title: componentData.title,
      keyPoints: keyPoints,
    };
  }

  render(inputs: Awaited<ReturnType<(typeof this)['fetchData']>>) {
    if (inputs) {
      return <KeyPointsColumn {...inputs} />;
    } else {
      return null;
    }
  }
}
